import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const normalizePhotos = (photos) => {
  if (!Array.isArray(photos)) return [];

  return photos
    .map((photo, index) => {
      if (typeof photo === 'string' && photo.trim()) {
        return { id: `photo-${index}`, url: photo };
      }
      if (photo && typeof photo === 'object' && typeof photo.url === 'string' && photo.url.trim()) {
        return { id: photo.id || `photo-${index}`, url: photo.url };
      }
      return null;
    })
    .filter(Boolean);
};

export const GuestGalleryLightbox = ({
  isOpen,
  photos,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  testIdPrefix = 'guest-gallery-lightbox',
}) => {
  const [touchStartX, setTouchStartX] = useState(null);
  const normalizedPhotos = useMemo(() => normalizePhotos(photos), [photos]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
      if (event.key === 'ArrowRight') onNext?.();
      if (event.key === 'ArrowLeft') onPrev?.();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNext, onPrev]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen || normalizedPhotos.length === 0 || currentIndex === null || currentIndex < 0 || currentIndex >= normalizedPhotos.length) {
    return null;
  }

  const currentPhoto = normalizedPhotos[currentIndex];

  const handleTouchStart = (event) => {
    setTouchStartX(event.changedTouches?.[0]?.clientX ?? null);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? null;
    if (endX === null) return;

    const deltaX = endX - touchStartX;
    if (Math.abs(deltaX) > 45) {
      if (deltaX < 0) onNext?.();
      if (deltaX > 0) onPrev?.();
    }

    setTouchStartX(null);
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/90 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      data-testid={`${testIdPrefix}-overlay`}
    >
      <div className="mx-auto flex h-full max-w-7xl items-center justify-center gap-2" data-testid={`${testIdPrefix}-container`}>
        <button
          type="button"
          onClick={onPrev}
          className="rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
          data-testid={`${testIdPrefix}-prev-button`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="relative flex-1 text-center" data-testid={`${testIdPrefix}-image-wrap`}>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 top-0 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
            data-testid={`${testIdPrefix}-close-button`}
          >
            <X className="h-5 w-5" />
          </button>

          <img
            src={currentPhoto.url}
            alt={`Guest gallery photo ${currentIndex + 1}`}
            className="mx-auto max-h-[85vh] max-w-full rounded-sm object-contain"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            data-testid={`${testIdPrefix}-active-image`}
          />

          <p className="mt-3 text-sm text-white/85" data-testid={`${testIdPrefix}-counter`}>
            {currentIndex + 1} of {normalizedPhotos.length}
          </p>
        </div>

        <button
          type="button"
          onClick={onNext}
          className="rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
          data-testid={`${testIdPrefix}-next-button`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
